import { AnimatedSprite, BlurFilter, Container, Graphics, type Texture } from 'pixi.js';
import type { CharacterAnimationName, CharacterTimings } from '../characters/types';
import { playSfx as playSfxClip } from './utils/audio';

export type { CharacterAnimationName } from '../characters/types';

// Loaded textures per animation slot (mirrors the path-based CharacterAssets in
// the character bundle, but post-load).
export type CharacterAnimations = {
  idle: Texture[];
  block?: Texture[];
  run?: Texture[];
  punch?: Texture[];
  kick?: Texture[];
  pain?: Texture[];
  painAir?: Texture[];
  dodge?: Texture[];
};

// Audio clip per animation slot. Played when the corresponding visual fires
// (e.g. 'pain' SFX plays when the renderer triggers the hit/pain animation).
export type CharacterSfx = Partial<Record<CharacterAnimationName, HTMLAudioElement>>;

// Common opts for both playAnimation (named slot) and playFrames (raw frames).
export type PlayAnimationOpts = {
  loop?: boolean;
  speed?: number;
  // Pause on a specific frame (0-indexed) for N wall-time milliseconds, then
  // resume. Fires on every loop pass through that frame.
  pauseAtFrame?: { frame: number; durationMs: number };
  // Multiplier on top of the natural height-fit. Use to compensate when an
  // animation sheet has the character drawn smaller relative to its frame
  // (e.g. crouched poses leave more empty padding above the head).
  scaleMultiplier?: number;
  // Body-local nudge applied to the inner sprite. Compensates for sheets that
  // frame the character at different positions (e.g. wider pain frames). Both
  // default to 0 (re-center on every swap). X mirrors with facing.
  offsetX?: number;
  offsetY?: number;
};

export type CharacterSpriteOptions = {
  facing: 1 | -1;
  // Placeholder fallback (used when no animations are provided).
  color: number;
  width?: number;
  height?: number;
  // Animated mode - frames per state.
  animations?: CharacterAnimations;
  animationSpeed?: number;
  // Optional SFX clips played alongside their matching animation.
  sfx?: CharacterSfx;
  // Per-animation timing tuning (frame speeds, impact frames, hold durations).
  // Adapter reads these to time hits, holds, and cycle speeds.
  timings?: CharacterTimings;
};

const DEFAULT_W = 100;
const DEFAULT_H = 150;
// Frames-per-tick. Effective fps is also dampened by Pixi ticker speed
// (we tie ticker to BATTLE_PACE_SCALE in main.ts via sharedTicker:true).
// At BATTLE_PACE_SCALE=4.5: 1.0 * 60Hz * (1/4.5) = ~13fps => idle cycle ~1.2s.
const DEFAULT_ANIM_SPEED = 1.0;

// Flat ground-cast shadow under the feet. Renders behind the body so it's
// visually under the character. Follows lunge/dodge/counter automatically since
// it's a child of CharacterSprite (which is what gets gsap-tweened).
const SHADOW_RADIUS_X = 52;
const SHADOW_RADIUS_Y = 8;
const SHADOW_Y_OFFSET = 6;
// Negative X = backward (away from facing direction). Compensates for the
// character art having feet slightly offset from the frame's horizontal center.
const SHADOW_X_OFFSET = -8;
const SHADOW_ALPHA = 0.45;
const SHADOW_BLUR = 6;

// `body` is a transform container - external animations (squish, scale) operate
// on `body.scale`. The actual visual (Graphics or AnimatedSprite) lives inside as
// `inner` and keeps its own intrinsic scale (e.g. fit-to-height for sprites).
// The CharacterSprite container itself is mirrored via scale.x = facing.
export class CharacterSprite extends Container {
  readonly body: Container;
  private readonly inner: Graphics | AnimatedSprite;
  private readonly defaultColor: number;
  private readonly animations: CharacterAnimations | null;
  private readonly sfx: CharacterSfx;
  private readonly defaultAnimationSpeed: number;
  // Public for adapter access; treat as readonly.
  readonly timings: CharacterTimings;
  // Tracks the setTimeout from the most recent pauseAtFrame so it can be
  // cancelled if a new animation (or reverse playback) starts before it fires.
  private pauseTimeoutId: ReturnType<typeof setTimeout> | null = null;
  readonly facing: 1 | -1;
  readonly bodyWidth: number;
  readonly bodyHeight: number;

  baseX = 0;
  baseY = 0;

  constructor(opts: CharacterSpriteOptions) {
    super();
    this.facing = opts.facing;
    this.defaultColor = opts.color;
    this.bodyWidth = opts.width ?? DEFAULT_W;
    this.bodyHeight = opts.height ?? DEFAULT_H;
    this.defaultAnimationSpeed = opts.animationSpeed ?? DEFAULT_ANIM_SPEED;
    this.animations = opts.animations ?? null;
    this.sfx = opts.sfx ?? {};
    this.timings = opts.timings ?? {};

    // Mirror by flipping the whole container. Children inside render normally.
    this.scale.x = this.facing;

    // Flat shadow at the feet - added FIRST so it renders behind the body.
    // BlurFilter softens the edge so it reads as a real ground-cast shadow.
    const shadow = new Graphics()
      .ellipse(SHADOW_X_OFFSET, -SHADOW_Y_OFFSET, SHADOW_RADIUS_X, SHADOW_RADIUS_Y)
      .fill({ color: 0x000000, alpha: SHADOW_ALPHA });
    shadow.filters = [new BlurFilter({ strength: SHADOW_BLUR })];
    this.addChild(shadow);

    this.body = new Container();
    this.addChild(this.body);

    if (this.animations && this.animations.idle.length > 0) {
      const sprite = new AnimatedSprite(this.animations.idle);
      sprite.anchor.set(0.5, 1); // pivot at bottom-center, like placeholder rect
      const fitScale = this.bodyHeight / sprite.texture.height;
      sprite.scale.set(fitScale);
      sprite.animationSpeed = this.defaultAnimationSpeed;
      sprite.loop = true;
      sprite.play();
      this.inner = sprite;
    } else {
      const g = new Graphics()
        .rect(-this.bodyWidth / 2, -this.bodyHeight, this.bodyWidth, this.bodyHeight)
        .fill(this.defaultColor);
      this.inner = g;
    }
    this.body.addChild(this.inner);
  }

  setHomePosition(x: number, y: number): void {
    this.baseX = x;
    this.baseY = y;
    this.position.set(x, y);
  }

  // Flips the sprite horizontally relative to its default facing.
  // Use during e.g. backward run so the running anim's leg motion matches the
  // direction of movement instead of the direction the character "looks at".
  // Pass false to restore the default facing-aligned orientation.
  setMirrored(mirrored: boolean): void {
    this.scale.x = this.facing * (mirrored ? -1 : 1);
  }

  // World-space Y of the top of the body (above the head). Used to spawn floating
  // text just above the character.
  get topY(): number {
    return this.y - this.bodyHeight;
  }

  // True when this character has the requested animation registered (and is in
  // animated mode at all). Use to choose between sprite-anim path and the
  // Graphics-placeholder fallback in the adapter.
  hasAnimation(name: CharacterAnimationName): boolean {
    return !!this.animations && !!this.animations[name];
  }

  // Plays the SFX clip registered for this slot, if any. Volume is the global
  // SFX master via playSfxClip helper.
  playSfx(name: CharacterAnimationName): void {
    const audio = this.sfx[name];
    if (!audio) return;
    playSfxClip(audio);
  }

  // Resolves once the AnimatedSprite advances to the given frame index.
  // Useful for awaiting "impact moments" inside a one-shot animation. If the
  // sprite already passed the frame, resolves immediately. No-op for the
  // Graphics placeholder.
  awaitFrame(targetFrame: number): Promise<void> {
    if (!(this.inner instanceof AnimatedSprite)) return Promise.resolve();
    const sprite = this.inner;
    if (sprite.currentFrame >= targetFrame) return Promise.resolve();
    return new Promise((resolve) => {
      sprite.onFrameChange = (currentFrame) => {
        if (currentFrame >= targetFrame) {
          sprite.onFrameChange = undefined;
          resolve();
        }
      };
    });
  }

  // Resolves when a non-looping AnimatedSprite finishes its last frame. If the
  // animation is looping or already finished, resolves immediately.
  awaitAnimationComplete(): Promise<void> {
    if (!(this.inner instanceof AnimatedSprite)) return Promise.resolve();
    const sprite = this.inner;
    if (sprite.loop) return Promise.resolve();
    if (!sprite.playing) return Promise.resolve();
    return new Promise((resolve) => {
      sprite.onComplete = () => {
        sprite.onComplete = undefined;
        resolve();
      };
    });
  }

  // Switches the AnimatedSprite to the requested named animation slot.
  // No-op for graphics placeholder or if the requested animation wasn't supplied.
  // Pulls per-anim offsets from `timings` so the inner sprite snaps to the
  // correct body-local position for this sheet (caller can still override).
  playAnimation(name: CharacterAnimationName, opts?: PlayAnimationOpts): void {
    if (!this.animations) return;
    const frames = this.animations[name];
    if (!frames || frames.length === 0) return;
    const t = this.timings[name];
    // Use ?? per-field instead of object spread - if a caller ever passes
    // `opts.offsetX = undefined` explicitly, spread would clobber the timings
    // value with undefined, but ?? keeps the timings fallback.
    this.playFrames(frames, {
      ...opts,
      offsetX: opts?.offsetX ?? t?.offsetX,
      offsetY: opts?.offsetY ?? t?.offsetY,
    });
  }

  // Plays an arbitrary frame array (not tied to the character's named slots).
  // Used by skill render hooks that load their own sheets (e.g. cannon shot).
  playFrames(frames: Texture[], opts?: PlayAnimationOpts): void {
    if (!(this.inner instanceof AnimatedSprite)) return;
    if (frames.length === 0) return;
    const sprite = this.inner;
    // Cancel any pending pause-resume from the previous animation.
    this.clearPendingPause();
    sprite.textures = frames;
    // Different sheets may have different frame heights - re-fit to bodyHeight
    // on every swap so the character keeps its on-screen size.
    const firstFrame = frames[0];
    if (firstFrame) {
      const baseScale = this.bodyHeight / firstFrame.height;
      sprite.scale.set(baseScale * (opts?.scaleMultiplier ?? 1));
    }
    // Position = per-character baseline anchor nudge (timings.anchorOffsetX,
    // applies to EVERY anim/skill) + per-sheet fine-tune (opts.offsetX).
    // Applied unconditionally so swapping back to a sheet without a per-sheet
    // offset re-centers (no leftover position from the previous swap).
    sprite.position.set(
      (this.timings.anchorOffsetX ?? 0) + (opts?.offsetX ?? 0),
      opts?.offsetY ?? 0,
    );
    sprite.loop = opts?.loop ?? true;
    sprite.animationSpeed = opts?.speed ?? this.defaultAnimationSpeed;

    // Reset any frame-pause handler from a previous animation.
    sprite.onFrameChange = undefined;
    if (opts?.pauseAtFrame) {
      const { frame: pauseFrame, durationMs } = opts.pauseAtFrame;
      sprite.onFrameChange = (currentFrame) => {
        if (currentFrame !== pauseFrame) return;
        sprite.stop();
        this.pauseTimeoutId = setTimeout(() => {
          this.pauseTimeoutId = null;
          if (sprite.textures === frames) sprite.play();
        }, durationMs);
      };
    }

    sprite.gotoAndPlay(0);
  }

  // Plays the currently set animation in REVERSE from its current frame back to
  // frame 0, then resolves. Bypasses any pause-at-frame from the original
  // playAnimation call - useful for "exit stance" transitions (e.g. coming out
  // of a held block when the attacker retreats).
  // Implementation: instead of relying on Pixi's negative animationSpeed (which
  // is unreliable with onComplete), we slice the texture array up to the
  // current frame, reverse it, and play forward through that sequence.
  playReverseToStart(opts?: { speed?: number }): Promise<void> {
    if (!(this.inner instanceof AnimatedSprite)) return Promise.resolve();
    const sprite = this.inner;
    const startFrame = sprite.currentFrame;
    if (startFrame === 0) return Promise.resolve();
    this.clearPendingPause();
    sprite.onFrameChange = undefined;
    // Frames 0..startFrame in reverse order. Last index is original frame 0.
    const reversed = sprite.textures.slice(0, startFrame + 1).reverse();
    sprite.textures = reversed;
    sprite.loop = false;
    sprite.animationSpeed = Math.abs(opts?.speed ?? this.defaultAnimationSpeed);
    sprite.gotoAndPlay(0);
    return new Promise((resolve) => {
      sprite.onComplete = () => {
        sprite.onComplete = undefined;
        resolve();
      };
    });
  }

  // Schedules an automatic snap to idle when the current non-looping anim
  // naturally completes. Use after starting a one-shot anim that you only
  // partially await (e.g. counter punch awaited up to impact frame - recovery
  // frames keep playing and would freeze on the last frame without this).
  // Guards against the textures being swapped meanwhile - if a different anim
  // takes over before this one ends, the snap is silently skipped.
  scheduleIdleAfterCurrentAnim(): void {
    if (!(this.inner instanceof AnimatedSprite)) return;
    const sprite = this.inner;
    if (sprite.loop) return; // looping anims never trigger onComplete
    const expectedTextures = sprite.textures;
    sprite.onComplete = () => {
      sprite.onComplete = undefined;
      if (sprite.textures !== expectedTextures) return;
      this.playAnimation('idle', { speed: this.timings.idle?.speed });
    };
  }

  private clearPendingPause(): void {
    if (this.pauseTimeoutId !== null) {
      clearTimeout(this.pauseTimeoutId);
      this.pauseTimeoutId = null;
    }
  }

  // Live-tunes AnimatedSprite playback speed without restarting it. Used to
  // sync leg cycle with body velocity during eased lunges (constant leg speed
  // while the body decelerates reads as "running in place"). No-op for the
  // Graphics placeholder.
  setAnimationSpeed(speed: number): void {
    if (this.inner instanceof AnimatedSprite) {
      this.inner.animationSpeed = speed;
    }
  }

  setBodyColor(color: number): void {
    if (this.destroyed) return;
    if (this.inner instanceof AnimatedSprite) {
      this.inner.tint = color;
    } else {
      this.redrawPlaceholder(color);
    }
  }

  // Cleanup pair for setBodyColor - returns visual to its untinted/default look.
  // Does NOT touch scale (so e.g. a BLOCK squish stance survives a HIT flash).
  // Guarded against running on a destroyed sprite - it can fire from the
  // delayed HIT_FLASH setTimeout after the scene is torn down (ESC-to-quit).
  restoreBodyColor(): void {
    if (this.destroyed) return;
    if (this.inner instanceof AnimatedSprite) {
      this.inner.tint = 0xffffff;
    } else {
      this.redrawPlaceholder(this.defaultColor);
    }
  }

  resetBody(): void {
    if (this.inner instanceof AnimatedSprite) {
      this.inner.tint = 0xffffff;
    } else {
      this.redrawPlaceholder(this.defaultColor);
    }
    this.body.scale.set(1, 1);
  }

  private redrawPlaceholder(color: number): void {
    if (!(this.inner instanceof Graphics)) return;
    this.inner
      .clear()
      .rect(-this.bodyWidth / 2, -this.bodyHeight, this.bodyWidth, this.bodyHeight)
      .fill(color);
  }

  // Clear the pending frame-pause timeout so it can't fire play() on a
  // destroyed sprite after the scene is torn down (ESC-to-quit mid-block).
  override destroy(options?: Parameters<Container['destroy']>[0]): void {
    this.clearPendingPause();
    super.destroy(options);
  }
}
